import React, { CSSProperties, useRef, useState } from "react";
import { classArr } from "../helper/style-helper";
import { useDelayHover } from "../helper/hook-helper";
import LoadingDots from "./loading-dots";
import BottomTooltip from "./bottom-tooltip";
import * as styles from "./velavu-button.module.scss";

export type VelavuButtonSize = "small" | "medium" | "large";

export type VelavuButtonVariant =
	"normal" | "outlined" | "danger" | "success" | "subtle" | "plain";

export interface VelavuButtonProps {
	style?: CSSProperties;
	className?: string;
	//The text to display on the button
	label?: string;
	//An icon to be applied with the text
	icon?: (size: number, color: string, className: string) => React.ReactNode;
	//The button's size
	size?: VelavuButtonSize;
	//The button's color scheme
	variant?: VelavuButtonVariant;
	//Whether this button should expand to fill horizontal space
	fullWidth?: boolean;
	//Whether this button should be disabled
	disabled?: boolean;
	//A callback when this button is clicked
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	//Append type=submit to enable this button to submit forms
	submit?: boolean;
	//Whether this button's text content should be replaced with a loading indicator
	loadingIndicator?: boolean;
	//Tooltip text to display on hover
	tooltip?: string;
}

//A reusable button component that conforms to Velavu's design system
export default function VelavuButton(props: VelavuButtonProps) {
	const [isHover, enableHover, disableHover] = useDelayHover();
	const styleList: string[] = [styles.main];
	let iconSize: number;

	//Set the button's size style, defaulting to medium
	switch (props.size ?? "medium") {
		case "small":
			styleList.push(styles.shapeSmall);
			iconSize = 16;
			break;
		case "medium":
			styleList.push(styles.shapeMedium);
			iconSize = 24;
			break;
		case "large":
			styleList.push(styles.shapeLarge);
			iconSize = 28;
			break;
	}

	switch (props.variant) {
		case "danger":
			styleList.push(styles.variantDanger);
			break;
		case "success":
			styleList.push(styles.variantSuccess);
			break;
		case "outlined":
			styleList.push(styles.variantOutlined);
			break;
		case "subtle":
			styleList.push(styles.variantSubtle);
			break;
		case "plain":
			styleList.push(styles.variantPlain);
			break;
		case "normal":
		default:
			styleList.push(styles.variantNormal);
			break;
	}

	//Set the button's full width appearance
	if (props.fullWidth) {
		styleList.push(styles.wide);
	}

	//Set the button's disabled appearance
	if (props.disabled) {
		styleList.push(styles.disabled);
	} else {
		styleList.push(styles.hover);
	}

	if (props.icon && !props.label) {
		styleList.push(styles.onlyIcon);
	}

	let type: "submit" | "reset" | "button" | undefined;
	if (props.submit === true) type = "submit";
	else if (props.submit === false) type = "button";
	else type = undefined;

	const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

	return (
		<>
			<button
				ref={setButtonRef}
				style={props.style}
				className={classArr(...styleList, props.className)}
				disabled={props.disabled}
				onClick={props.onClick}
				type={type}
				onMouseEnter={enableHover}
				onMouseLeave={disableHover}
			>
				{props.icon &&
					props.icon(
						iconSize,
						props.loadingIndicator ? "transparent" : "currentColor",
						styles.icon,
					)}
				{props.label && (
					<span
						className={classArr(
							styles.labelMargin,
							props.icon && styles.iconLabelMargin,
							props.loadingIndicator && styles.loadingHidden,
						)}
					>
						{props.label}
					</span>
				)}
				{props.loadingIndicator && (
					<LoadingDots
						className={styles.loadingIndicator}
						color="currentColor"
					/>
				)}
			</button>

			{isHover && !props.disabled && props.tooltip && (
				<BottomTooltip refPosition={buttonRef}>
					{props.tooltip}
				</BottomTooltip>
			)}
		</>
	);
}
